/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import Hammer from 'hammerjs';

// Import assets
import './zoomCanvas.scss';

// TODO: When zooming in the image is always scaled with the center of the image as the origin.
// When zoomed in by half you can move the image so that you look at the top left corner for example.
// When zooming further in from that position, the image should stay focused on the currently visible part of the image.
// What happens instead, is, that the image is seemingly moved up and left while zooming because the transform origin
// is still the center of the image and not the center of the canvas.

// TODO: The maxZoomLevel-prop is currently fixed. With that, an image can be zoomed in way further than to its 100% size.

class ZoomCanvas extends React.Component {

  // # Static Helper

  static clampNumber(value, min, max) {
    let result = value;
    result = Math.min(result, max);
    result = Math.max(result, min);
    return result;
  }

  static getInitialSource(sources) {
    const width = window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth;
    let src = sources.default;

    const sizesAvailable = Object.keys(sources).filter(size => !isNaN(parseInt(size, 10))).map(size => parseInt(size, 10));
    let size = 0;

    while (sizesAvailable[size] < width && size < sizesAvailable.length - 1) {
      size += 1;
    }

    if (sizesAvailable.length > 0) {
      src = sources[sizesAvailable[size].toString(10)];
    }

    return src;
  }

  static getZoomSource(sources) {
    return sources.default;
  }

  static restrictPositionInBoundaries(frameWidth, frameHeight, boxWidth, boxHeight, boxCenterX = 0, boxCenterY = 0) {
    const result = {
      x: boxCenterX,
      y: boxCenterY,
    };

    const xOverflow = boxWidth - frameWidth;
    const yOverflow = boxHeight - frameHeight;

    if (xOverflow < 0) { // Box is smaller than frame
      result.x = 0;
    } else { // Box is bigger than the frame
      result.x = ZoomCanvas.clampNumber(result.x, -(xOverflow / 2), xOverflow / 2);
    }

    if (yOverflow < 0) { // Box is smaller than frame
      result.y = 0;
    } else { // Box is bigger than the frame
      result.y = ZoomCanvas.clampNumber(result.y, -(yOverflow / 2), yOverflow / 2);
    }

    return result;
  }

  // # Lifecycle Methods

  constructor(props) {
    super(props);

    // The usual component-state
    this.state = {
      initialImageSrc: ZoomCanvas.getInitialSource(this.props.sources),
      imageIsZoomed: false,
    };

    // The canvasState is separate from the component-state (above) but works in a similar way.
    // This object is used to handle the rerendering of canvas-related stuff in a renderCanvas() method.
    // See this.setCanvasState() and this.renderCanvas() methods
    this.canvasState = {
      dragged: false,
      zoomLevel: 0,
      scale: 1,
      x: 0, // Image positioning is done by aligning the center of the image to the canvas-center. ...
      y: 0, // ... So these coordinates are relative to the middle. This makes it easier to reset the image-position.
    };

    // Binding "this" to instance-methods
    this.handleImageLoad = this.handleImageLoad.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handlePan = this.handlePan.bind(this);
    this.handlePanStart = this.handlePanStart.bind(this);
    this.handlePinch = this.handlePinch.bind(this);
    this.handlePinchStart = this.handlePinchStart.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleZoomImageLoad = this.handleZoomImageLoad.bind(this);
    this.renderCanvas = this.renderCanvas.bind(this);
    this.resetImage = this.resetImage.bind(this);
    this.updateCanvas = this.updateCanvas.bind(this);
    this.updateCaption = this.updateCaption.bind(this);
    this.updateImage = this.updateImage.bind(this);
    this.zoom = this.zoom.bind(this);
  }

  componentDidMount() {
    this.updateCanvas(); // To set the initial canvas dimensions

    // Bind Hammer-Events on canvas
    this.hammer = new Hammer(this.canvas);
    const pinch = new Hammer.Pinch();
    const pan = new Hammer.Pan();

    pinch.recognizeWith(pan);

    this.hammer.add([pinch, pan]);
    this.hammer.on('pinch', this.handlePinch);
    this.hammer.on('pinchstart', this.handlePinchStart);
    this.hammer.on('pan', this.handlePan);
    this.hammer.on('panstart', this.handlePanStart);

    // Bind other events
    window.addEventListener('resize', this.handleResize);
    this.canvas.addEventListener('mouseup', this.handleMouseUp, false);
    this.canvas.addEventListener('DOMMouseScroll', this.handleScroll, false);
    this.canvas.addEventListener('mousewheel', this.handleScroll, false);
    this.image.onload = this.handleImageLoad;
    this.zoomImage.onload = this.handleZoomImageLoad;
  }

  componentWillUnmount() {
    // Unbind Hammer-Events
    if (this.hammer) {
      this.hammer.stop();
      this.hammer.destroy();
    }
    this.hammer = null;

    // Unbind other events
    window.removeEventListener('resize', this.handleResize);
    this.canvas.removeEventListener('mouseup', this.handleMouseUp);
    this.canvas.removeEventListener('DOMMouseScroll', this.handleScroll);
    this.canvas.removeEventListener('mousewheel', this.handleScroll);
  }

  // # Handler

  handleImageLoad() {
    this.setCanvasState({
      image: this.canvasState.image || this.image, // If there already is an image, it is probably the bigger one. we don't want to replace it with a smaller in that case.
    });

    this.updateImage();
    this.resetImage();
    this.props.onLoad();
  }

  handleZoomImageLoad() {
    this.setCanvasState({
      image: this.zoomImage,
    });
  }

  handleScroll(evt) {
    evt.preventDefault();
    let delta = evt.detail ? -evt.detail : 0;
    if (evt.wheelDelta) {
      delta = evt.wheelDelta / 120;
    }
    if (delta) {
      this.zoom(delta);
    }
  }

  handleResize() {
    this.updateCanvas();
    this.updateImage();
  }

  handleMouseUp(evt) {
    if (!this.canvasState.dragged) {
      this.zoom(evt.shiftKey ? -1 : 1);
    }
    this.setCanvasState({
      dragged: false,
    });
  }

  handlePinch(evt) {
    const pinchDelta = evt.scale - this.pinchDeltaCache;
    this.pinchDeltaCache = evt.scale;

    // pinchDelta is multiplied with a scale-factor because the scale-movement is to slow otherwise.
    // It has to be different for pinchin and pinchout.
    // Those values feel best. (for pinchin it's doubled)
    this.zoom(pinchDelta * (evt.additionalEvent === 'pinchin' ? 12 : 6));
  }

  handlePinchStart(evt) {
    this.pinchDeltaCache = 1;
  }

  handlePan(evt) {
    // Calculate the delta movement from this pan-event to the prior pan-event.
    const panDeltaX = evt.deltaX - this.panDeltaXCache;
    const panDeltaY = evt.deltaY - this.panDeltaYCache;
    // Update the overall movement for this pan-motion for the previous two lines in the next pan-event.
    this.panDeltaXCache = evt.deltaX;
    this.panDeltaYCache = evt.deltaY;
    // Add the new movement to the current image-position
    let newX = this.canvasState.x + panDeltaX;
    let newY = this.canvasState.y + panDeltaY;

    // Restrict the new coordinates to boundaries resulting in an object: { x: <number>, y: <number> }
    const res = ZoomCanvas.restrictPositionInBoundaries(this.canvasState.canvasWidth, this.canvasState.canvasHeight, this.canvasState.imageWidth * this.canvasState.scale, this.canvasState.imageHeight * this.canvasState.scale, newX, newY);

    // Update the canvas-state with the new coordinates.
    this.setCanvasState(res);
  }

  handlePanStart(evt) {
    // Reset the delta-caches for the upcoming pan-movement (handlePan depends on it)
    this.panDeltaXCache = 0;
    this.panDeltaYCache = 0;

    this.setCanvasState({
      dragged: true,
    });
  }

  // # Helper

  // Works similar to the ReactComponent.setState but will call the renderCanvas instead of the ReactComponent.render method.
  setCanvasState(stateUpdate = {}) {
    this.canvasState = this.canvasState || {};
    const oldState = { ...this.canvasState };
    this.canvasState = { ...oldState, ...stateUpdate };
    this.renderCanvas(oldState);
  }

  /* public */
  zoom(zoomLevelDelta) {
    let newZoomLevel = this.canvasState.zoomLevel + zoomLevelDelta;
    newZoomLevel = ZoomCanvas.clampNumber(newZoomLevel, this.props.minZoomLevel, this.props.maxZoomLevel)

    const newScale = this.props.scaleFactor ** newZoomLevel;
    const newPosition = ZoomCanvas.restrictPositionInBoundaries(this.canvasState.canvasWidth, this.canvasState.canvasHeight, this.canvasState.imageWidth * newScale, this.canvasState.imageHeight * newScale, this.canvasState.x, this.canvasState.y);

    this.setState({
      imageIsZoomed: newZoomLevel !== 0,
    });

    this.setCanvasState({
      zoomLevel: newZoomLevel,
      scale: newScale,
      x: newPosition.x,
      y: newPosition.y,
    });

    if (newZoomLevel > 0 && !this.state.zoomImageSrc) {
      this.setState({
        zoomImageSrc: ZoomCanvas.getZoomSource(this.props.sources),
      });
    } else if (newZoomLevel === this.props.minZoomLevel) {
      this.resetImage();
    }
  }

  updateCanvas() {
    // Updating the dimensions on the canvas
    this.canvas.width = this.wrapper.offsetWidth;
    this.canvas.height = this.wrapper.offsetHeight;
    this.setCanvasState({
      canvasWidth: this.canvas.width,
      canvasHeight: this.canvas.height,
    });
  }

  updateCaption() {
    if (this.canvasState.imageWidth) {
      this.setState({
        captionOffsetX: (this.canvas.width - this.canvasState.imageWidth) / 2,
      });
    }
  }

  updateImage() {
    const imageRatio = this.image.width / this.image.height;

    let imageWidth = this.canvasState.canvasWidth;
    let imageHeight = imageWidth / imageRatio;

    if (imageHeight > this.canvasState.canvasHeight) {
      imageHeight = this.canvasState.canvasHeight;
      imageWidth = imageHeight * imageRatio;
    }

    this.setCanvasState({
      imageRatio,
      imageWidth,
      imageHeight,
    });

    this.updateCaption();
  }

  /* public */
  resetImage() {
    this.setState({
      imageIsZoomed: false,
    });
    this.setCanvasState({
      zoomLevel: 0,
      scale: 1,
      x: 0,
      y: 0,
    });
  }

 // # Renderer

  renderCanvas() {
    if (!this.ctx) {
      this.ctx = this.canvas.getContext('2d');
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';
    }

    const { image, canvasWidth, canvasHeight, imageWidth, imageHeight, x, y, scale } = this.canvasState;

    if (canvasWidth && canvasHeight && imageWidth && imageHeight) {
      // Setting context to top left corner with scale 1 ...
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      // ... to clear the whole canvas.
      this.ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      // Then setting the context to the middle of the canvas ...
      this.ctx.translate(canvasWidth / 2, canvasHeight / 2);
      // and move it to the correct position offset from there (as moved by the user) ...
      this.ctx.translate(x || 0, y || 0);
      // ... scale it ...
      this.ctx.scale(scale || 1, scale || 1);
      // ... and draw the image.
      this.ctx.drawImage(image, -(imageWidth / 2), -(imageHeight / 2), imageWidth, imageHeight);
    }
  }

  render() {
    let className = 'm-zoom-canvas';
    if (this.state.imageIsZoomed) {
      className += ' m-zoom-canvas--draggable';
    }
    return (
      <div
        className={className}
        ref={(ref) => {
          this.wrapper = ref;
        }}
      >
        <canvas
          className={'m-zoom-canvas__canvas'}
          ref={(ref) => {
            this.canvas = ref;
          }}
        />
        <img
          className={'m-zoom-canvas__pic'}
          src={this.state.initialImageSrc}
          alt={this.props.alt}
          ref={(ref) => {
            this.image = ref;
          }}
        />
        <img
          className={'m-zoom-canvas__pic'}
          src={this.state.zoomImageSrc}
          alt={this.props.alt}
          ref={(ref) => {
            this.zoomImage = ref;
          }}
        />
        {this.props.description && typeof this.state.captionOffsetX === 'number' && (
          <span
            className={'m-zoom-canvas__img-desc'}
            style={{ left: `${this.state.captionOffsetX}px` }}
            dangerouslySetInnerHTML={{ __html: this.props.description }}
          />
        )}
        <div className={'m-zoom-canvas__img-hint'}>
          Click to zoom in and load a high resolution version of the image.
        </div>
      </div>
    );
  }
}

ZoomCanvas.propTypes = {
  sources: PropTypes.shape(),
  alt: PropTypes.string,
  onLoad: PropTypes.func,
  description: PropTypes.string,
  scaleFactor: PropTypes.number,
  minZoomLevel: PropTypes.number,
  maxZoomLevel: PropTypes.number,
};

ZoomCanvas.defaultProps = {
  scaleFactor: 1.1, // scaleFactor^zoomLevel = scale => scaleFactor(1.1)^maxZoomLevel(25) = max-scale–of-image(10,8347059434)
  minZoomLevel: 0,
  maxZoomLevel: 25,
  sources: {},
  alt: '',
  onLoad: () => {},
};

export default ZoomCanvas;



// WEBPACK FOOTER //
// ./frontend/js/Components/Globals/ImagePlayer/ZoomCanvas.jsx